import React, { useState } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { base44 } from '@/api/base44Client';

const EXPERIENCES = [
  { value: 'adult_sim_racing', label: 'Adult Sim Racing Experience' },
  { value: 'child_sim_racing', label: 'Child Sim Racing Experience' },
  { value: 'young_racer', label: 'Young Racer Experience' },
];

// 1-10 satisfaction ratings (two start at 0).
const RATINGS = [
  { key: 'q_overall', label: 'Overall Sim Racing Experience', min: 1 },
  { key: 'q_comfort', label: 'Comfort of seating & rig setup', min: 1 },
  { key: 'q_controls', label: 'Performance & feel of wheel, pedals & controls', min: 1 },
  { key: 'q_explanation', label: 'Equipment well-explained & easy to use', min: 1 },
  { key: 'q_staff', label: "Staff's friendliness & support", min: 1 },
  { key: 'q_guidance', label: 'Adequately guided throughout the experience', min: 0 },
  { key: 'q_value', label: 'Value for money', min: 0 },
];

const LIKELIHOOD = [
  { key: 'q_recommend', label: 'How likely are you to recommend us to a friend?' },
  { key: 'q_rebook', label: 'How likely are you to book another session?' },
];

const inputCls =
  'w-full bg-[#0d0d0f] border border-white/10 focus:border-primary text-white text-sm px-4 py-3 outline-none transition-colors placeholder:text-white/20';

function Scale({ value, onChange, min = 1, lowLabel, highLabel }) {
  const nums = [];
  for (let i = min; i <= 10; i++) nums.push(i);
  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {nums.map((n) => (
          <button
            type="button"
            key={n}
            onClick={() => onChange(n)}
            className={`w-9 h-9 text-xs font-bold border transition-colors ${
              value === n
                ? 'bg-primary border-primary text-white'
                : 'bg-[#0d0d0f] border-white/10 text-white/50 hover:border-primary/40'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="flex justify-between mt-2 text-[10px] text-white/30 font-bold tracking-widest">
        <span>{lowLabel}</span>
        <span>{highLabel}</span>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-white/40 text-xs font-bold tracking-widest mb-2">
        {label}
        {required && <span className="text-primary">*</span>}
      </label>
      {children}
    </div>
  );
}

function RatingBlock({ label, required, children }) {
  return (
    <div className="py-5 border-b border-white/5 last:border-0">
      <p className="text-white/70 text-sm font-medium mb-3">
        {label}
        {required && <span className="text-primary ml-1">*</span>}
      </p>
      {children}
    </div>
  );
}

export default function FeedbackForm({ initialBookingRef, initialExperience }) {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    booking_reference: initialBookingRef || '',
    experience_type: initialExperience || 'adult_sim_racing',
    date_of_experience: '',
    consent_marketing: false,
    q_overall: 0,
    q_comfort: 0,
    q_controls: 0,
    q_explanation: 0,
    q_staff: 0,
    q_guidance: null,
    q_value: null,
    improvements: '',
    q_recommend: 0,
    q_rebook: 0,
    further_comments: '',
    consent_draw: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const required = [
      'first_name', 'last_name', 'email', 'booking_reference', 'date_of_experience', 'improvements',
      ...RATINGS.map((r) => r.key), 'q_recommend', 'q_rebook',
    ];
    const missing = required.filter((k) => !form[k]);
    if (missing.length) {
      setError('Please complete all required fields before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      await base44.functions.invoke('submitExperienceFeedback', form);
      setDone(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="bg-[#111115] border border-white/5 p-12 text-center">
        <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-5" />
        <h3 className="text-white font-black text-2xl uppercase mb-3">Thank You</h3>
        <p className="text-white/50 text-sm max-w-md mx-auto leading-relaxed">
          Your feedback has been recorded
          {form.consent_draw
            ? ' and you have been entered into our monthly free-session draw. Good luck!'
            : '.'}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6">
      {/* details */}
      <div className="bg-[#111115] border border-white/5 p-6 md:p-8">
        <p className="text-primary text-[10px] font-bold tracking-widest mb-5">YOUR DETAILS</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="FIRST NAME" required>
            <input className={inputCls} value={form.first_name} onChange={(e) => set('first_name', e.target.value)} />
          </Field>
          <Field label="LAST NAME" required>
            <input className={inputCls} value={form.last_name} onChange={(e) => set('last_name', e.target.value)} />
          </Field>
          <Field label="EMAIL ADDRESS" required>
            <input type="email" className={inputCls} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="email@example.com" />
          </Field>
          <Field label="PHONE">
            <input className={inputCls} value={form.phone} onChange={(e) => set('phone', e.target.value)} />
          </Field>
          <Field label="BOOKING REFERENCE" required>
            <input className={inputCls} value={form.booking_reference} onChange={(e) => set('booking_reference', e.target.value)} placeholder="From your booking email" />
          </Field>
          <Field label="EXPERIENCE">
            <select className={inputCls} value={form.experience_type} onChange={(e) => set('experience_type', e.target.value)}>
              {EXPERIENCES.map((x) => (
                <option key={x.value} value={x.value} className="bg-[#111115]">{x.label}</option>
              ))}
            </select>
          </Field>
          <Field label="DATE OF EXPERIENCE" required>
            <input type="date" className={inputCls} value={form.date_of_experience} onChange={(e) => set('date_of_experience', e.target.value)} />
          </Field>
        </div>
        <label className="flex items-start gap-3 cursor-pointer mt-5">
          <input type="checkbox" checked={form.consent_marketing} onChange={(e) => set('consent_marketing', e.target.checked)} className="mt-1 w-4 h-4 accent-[#c0392b]" />
          <span className="text-white/60 text-sm">Subscribe me to your newsletter</span>
        </label>
      </div>

      {/* satisfaction ratings */}
      <div className="bg-[#111115] border border-white/5 p-6 md:p-8">
        <p className="text-primary text-[10px] font-bold tracking-widest mb-2">RATE YOUR EXPERIENCE</p>
        <p className="text-white/30 text-xs mb-2">On a scale of 1–10 (0 = not applicable)</p>
        {RATINGS.map((r) => (
          <RatingBlock key={r.key} label={r.label} required>
            <Scale
              value={form[r.key]}
              onChange={(v) => set(r.key, v)}
              min={r.min}
              lowLabel="Not satisfied"
              highLabel="Very satisfied"
            />
          </RatingBlock>
        ))}
      </div>

      {/* open text */}
      <div className="bg-[#111115] border border-white/5 p-6 md:p-8 space-y-5">
        <Field label="WHAT COULD WE IMPROVE TO ENHANCE YOUR NEXT EXPERIENCE?" required>
          <textarea rows={4} className={`${inputCls} resize-none`} value={form.improvements} onChange={(e) => set('improvements', e.target.value)} />
        </Field>
        {LIKELIHOOD.map((l) => (
          <RatingBlock key={l.key} label={l.label} required>
            <Scale
              value={form[l.key]}
              onChange={(v) => set(l.key, v)}
              min={1}
              lowLabel="Not likely"
              highLabel="Very likely"
            />
          </RatingBlock>
        ))}
        <Field label="ANY FURTHER COMMENTS OR SUGGESTIONS">
          <textarea rows={3} className={`${inputCls} resize-none`} value={form.further_comments} onChange={(e) => set('further_comments', e.target.value)} />
        </Field>
      </div>

      {/* draw opt-in */}
      <div className="bg-[#111115] border border-white/5 p-6 md:p-8">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={form.consent_draw} onChange={(e) => set('consent_draw', e.target.checked)} className="mt-1 w-4 h-4 accent-[#c0392b]" />
          <span className="text-white/60 text-sm leading-relaxed">
            Enter me into the monthly draw for a complimentary one-hour Sim Racing Experience.
          </span>
        </label>
      </div>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-primary/20 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-sm tracking-widest px-8 py-4 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'SUBMIT FEEDBACK'}
      </button>
    </form>
  );
}